'use client';
import { Cloud, Clouds } from '@react-three/drei';
import { ReactThreeFiber } from '@react-three/fiber';
import { shuffle } from 'lodash';
import { useMemo } from 'react';
import { MeshBasicMaterial, Vector3 } from 'three';

import { publicAssets } from 'utils/gcs';

export interface CloudGeneratorProps {
  pointsPerQuadrant: number;
  maxRange: number;
  minRange: number;
  height: number;

  cloudSegments?: number;
  segmentsLimit?: number;
  color?: ReactThreeFiber.Color;
}

const CloudGenerator = ({
  cloudSegments = 20,
  height,
  maxRange,
  minRange,
  pointsPerQuadrant,
  segmentsLimit,
  color = 'darkgray',
}: CloudGeneratorProps) => {
  const SEGMENTS_LIMIT =
    typeof segmentsLimit === 'number'
      ? segmentsLimit
      : Math.floor(4 * pointsPerQuadrant * cloudSegments * (100 / 100));

  const RANDOM_POINTS = useMemo(
    () =>
      shuffle(
        Array.from(
          { length: 4 * pointsPerQuadrant },
          (_, i) =>
            new Vector3(
              (i % 2 === 0 ? 1 : -1) *
                (Math.random() * (maxRange - minRange) + minRange),

              height,

              (Math.floor(i / 2) % 2 === 0 ? 1 : -1) *
                (Math.random() * (maxRange - minRange) + minRange)
            )
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Clouds
      material={MeshBasicMaterial}
      texture={publicAssets('/cloud.png')}
      limit={SEGMENTS_LIMIT}
    >
      {RANDOM_POINTS.map((point, index) => (
        <Cloud
          key={index}
          scale={25}
          seed={index}
          color={color}
          castShadow={false}
          segments={cloudSegments}
          position={new Vector3(...point)}
          speed={(0.2 + index * 0.05) % 0.25}
          opacity={(0.1 + index * 0.05) % 0.2}
          rotation={[0, (index / (2 * pointsPerQuadrant)) * Math.PI * 2, 0]}
        />
      ))}
    </Clouds>
  );
};

export default CloudGenerator;
